'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

const STORAGE_KEY = 'aos-cookie-consent';

export type CookieConsentValue = 'accepted' | 'rejected' | null;

export function getStoredCookieConsent(): CookieConsentValue {
  if (typeof window === 'undefined') {
    return null;
  }
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === 'accepted' || v === 'rejected') {
    return v;
  }
  return null;
}

export function setStoredCookieConsent(value: Exclude<CookieConsentValue, null>) {
  window.localStorage.setItem(STORAGE_KEY, value);
  window.dispatchEvent(new Event('aos-cookie-consent-change'));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).cookie;

  useEffect(() => {
    setVisible(getStoredCookieConsent() === null);
    const onChange = () => setVisible(getStoredCookieConsent() === null);
    window.addEventListener('aos-cookie-consent-change', onChange);
    return () => window.removeEventListener('aos-cookie-consent-change', onChange);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border bg-card p-4 shadow-lg md:p-5"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="max-w-3xl text-sm text-foreground/85">
          <p id="cookie-banner-title" className="font-heading font-bold text-foreground">
            {copy.title}
          </p>
          <p className="mt-1 leading-relaxed">
            {copy.body}{' '}
            <Link
              href={localizePath('/privacy')}
              className="text-primary underline underline-offset-2"
            >
              {copy.privacyPolicy}
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-3">
          <button
            type="button"
            className="min-h-[44px] rounded-md border border-border px-5 text-xs font-black uppercase tracking-widest text-foreground hover:bg-muted"
            onClick={() => {
              setStoredCookieConsent('rejected');
              setVisible(false);
            }}
          >
            {copy.essentialOnly}
          </button>
          <button
            type="button"
            className="min-h-[44px] rounded-md bg-primary px-5 text-xs font-black uppercase tracking-widest text-white hover:bg-primary/90"
            onClick={() => {
              setStoredCookieConsent('accepted');
              setVisible(false);
            }}
          >
            {copy.acceptAnalytics}
          </button>
        </div>
      </div>
    </div>
  );
}
